import React, { useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import {
  GENERATE_ADMIN_PAYMENT_LINK,
  GET_PAYMENT_LINK_BOOKING_INFO,
} from "./graphql";
import EmployeeDepositPicker from "./EmployeeDepositPicker";
import { inr, formatDate, PURPOSE_LABELS } from "./format";

const OTHER_CATEGORIES = ["Other Income", "Training Fee", "Product Sale"];

/**
 * Generate a Razorpay payment link from the admin panel. The backend puts the
 * link's purpose and target in its notes, so when it's paid the webhook
 * records it against the booking / professional automatically.
 */
const GeneratePaymentLinkForm = ({ initialBookingId, onClose, onCreated }) => {
  const [purpose, setPurpose] = useState("BOOKING_PAYMENT");
  const [bookingId, setBookingId] = useState(initialBookingId || "");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState(OTHER_CATEGORIES[0]);
  const [customerName, setCustomerName] = useState("");
  const [customerContact, setCustomerContact] = useState("");
  const [employeeId, setEmployeeId] = useState(null);
  const [orderIds, setOrderIds] = useState([]);
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  const [expireInHours, setExpireInHours] = useState(24);
  const [error, setError] = useState("");
  const [created, setCreated] = useState(null);
  const [copied, setCopied] = useState(false);

  const [lookupBooking, { data: bookingData, loading: bookingLoading }] =
    useLazyQuery(GET_PAYMENT_LINK_BOOKING_INFO, { fetchPolicy: "network-only" });
  const booking =
    bookingData?.getPaymentLinkBookingInfo?.status === "SUCCESS"
      ? bookingData.getPaymentLinkBookingInfo.booking
      : null;
  const bookingError =
    bookingData?.getPaymentLinkBookingInfo?.status === "ERROR"
      ? bookingData.getPaymentLinkBookingInfo.message
      : "";

  const [generate, { loading: generating }] = useMutation(
    GENERATE_ADMIN_PAYMENT_LINK,
  );

  const handleLookup = () => {
    if (bookingId.trim()) {
      lookupBooking({ variables: { bookingId: bookingId.trim() } });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    const data = {
      purpose,
      notifyCustomer,
      expireInHours: Number(expireInHours) || 24,
    };
    if (purpose === "BOOKING_PAYMENT") {
      if (!bookingId.trim()) return setError("Enter a booking ID");
      data.bookingId = bookingId.trim();
      if (amount) data.amount = Number(amount);
    } else if (purpose === "EMPLOYEE_CASH_DEPOSIT") {
      if (!employeeId) return setError("Select a professional");
      if (!orderIds.length) return setError("Select the bookings being settled");
      data.employeeId = employeeId;
      data.orderIds = orderIds;
    } else {
      if (!amount || Number(amount) <= 0) return setError("Enter an amount");
      if (!description.trim()) return setError("Enter what this payment is for");
      data.amount = Number(amount);
      data.category = category;
      data.customerName = customerName || undefined;
      data.customerContact = customerContact || undefined;
    }
    if (description.trim()) data.description = description.trim();

    try {
      const res = await generate({ variables: { data } });
      const out = res.data?.generateAdminPaymentLink;
      if (out?.status !== "SUCCESS") return setError(out?.message || "Failed");
      setCreated(out.link);
      onCreated?.();
    } catch (err) {
      setError(err.message);
    }
    return null;
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(created.shortUrl);
      setCopied(true);
    } catch (e) {
      setCopied(false);
    }
  };

  if (created) {
    return (
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Payment link created</h2>
        <p className="text-sm text-gray-600">
          {PURPOSE_LABELS[created.purpose]} · {inr(created.amount)} ·{" "}
          {created.description}
        </p>
        <div className="flex gap-2">
          <input
            readOnly
            value={created.shortUrl || ""}
            className="flex-1 border rounded-md px-3 py-2 font-mono text-sm"
          />
          <button
            type="button"
            onClick={copy}
            className="px-4 py-2 bg-[#FBB033] rounded-md font-semibold"
          >
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
        {created.customerContact && notifyCustomer && (
          <p className="text-xs text-gray-500">
            Razorpay has also sent it by SMS to {created.customerContact}.
          </p>
        )}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border rounded-md"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">Generate payment link</h2>
        <button type="button" onClick={onClose} className="text-gray-500">
          ✕
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {Object.entries(PURPOSE_LABELS).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => {
              setPurpose(key);
              setError("");
            }}
            className={`px-3 py-2 rounded-md text-sm border ${
              purpose === key
                ? "bg-[#FBB033] border-[#FBB033] font-semibold"
                : "bg-white"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {purpose === "BOOKING_PAYMENT" && (
        <div className="space-y-3">
          <div className="flex gap-2 items-end">
            <label className="flex-1 block text-sm font-medium text-gray-700">
              Booking ID
              <input
                value={bookingId}
                onChange={(e) => setBookingId(e.target.value)}
                onBlur={handleLookup}
                placeholder="e.g. 261002-FGA7"
                className="mt-1 w-full border rounded-md px-3 py-2 font-mono"
              />
            </label>
            <button
              type="button"
              onClick={handleLookup}
              className="px-4 py-2 border rounded-md"
            >
              {bookingLoading ? "…" : "Find"}
            </button>
          </div>
          {bookingError && <p className="text-sm text-red-600">{bookingError}</p>}
          {booking && (
            <div className="text-sm bg-gray-50 border rounded-md p-3 grid grid-cols-2 gap-1">
              <span className="text-gray-500">Customer</span>
              <span>
                {booking.customerName} ({booking.customerContact})
              </span>
              <span className="text-gray-500">Service date / status</span>
              <span>
                {formatDate(booking.bookingDate)} · {booking.status}
              </span>
              <span className="text-gray-500">Price / paid online</span>
              <span>
                {inr(booking.price)} / {inr(booking.amountPaid)}
              </span>
              <span className="text-gray-500 font-medium">Due</span>
              <span className="font-semibold">{inr(booking.amountDue)}</span>
            </div>
          )}
          <label className="block text-sm font-medium text-gray-700">
            Amount (leave blank for the full due amount)
            <input
              type="number"
              min="1"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder={booking ? String(booking.amountDue) : ""}
              className="mt-1 w-full border rounded-md px-3 py-2"
            />
          </label>
        </div>
      )}

      {purpose === "EMPLOYEE_CASH_DEPOSIT" && (
        <EmployeeDepositPicker
          employeeId={employeeId}
          setEmployeeId={setEmployeeId}
          selectedOrderIds={orderIds}
          setSelectedOrderIds={setOrderIds}
        />
      )}

      {purpose === "OTHER" && (
        <div className="grid grid-cols-2 gap-3">
          <label className="block text-sm font-medium text-gray-700">
            Amount
            <input
              type="number"
              min="1"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 w-full border rounded-md px-3 py-2"
            />
          </label>
          <label className="block text-sm font-medium text-gray-700">
            Category
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="mt-1 w-full border rounded-md px-3 py-2"
            >
              {OTHER_CATEGORIES.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </label>
          <label className="block text-sm font-medium text-gray-700">
            Payer name
            <input
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              className="mt-1 w-full border rounded-md px-3 py-2"
            />
          </label>
          <label className="block text-sm font-medium text-gray-700">
            Payer phone
            <input
              value={customerContact}
              onChange={(e) => setCustomerContact(e.target.value)}
              placeholder="10-digit mobile"
              className="mt-1 w-full border rounded-md px-3 py-2"
            />
          </label>
        </div>
      )}

      <label className="block text-sm font-medium text-gray-700">
        Description {purpose === "OTHER" ? "" : "(optional)"}
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder={
            purpose === "OTHER" ? "What is this payment for?" : "Shown to the payer"
          }
          className="mt-1 w-full border rounded-md px-3 py-2"
        />
      </label>

      <div className="flex flex-wrap items-center gap-4 text-sm">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={notifyCustomer}
            onChange={(e) => setNotifyCustomer(e.target.checked)}
          />
          Send link by SMS
        </label>
        <label className="flex items-center gap-2">
          Expires in
          <select
            value={expireInHours}
            onChange={(e) => setExpireInHours(e.target.value)}
            className="border rounded-md px-2 py-1"
          >
            <option value={1}>1 hour</option>
            <option value={6}>6 hours</option>
            <option value={24}>24 hours</option>
            <option value={72}>3 days</option>
            <option value={168}>7 days</option>
          </select>
        </label>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="px-4 py-2 border rounded-md">
          Cancel
        </button>
        <button
          type="submit"
          disabled={generating}
          className="px-4 py-2 bg-[#FBB033] rounded-md font-semibold disabled:opacity-60"
        >
          {generating ? "Creating…" : "Create link"}
        </button>
      </div>
    </form>
  );
};

export default GeneratePaymentLinkForm;
